// @flow

import React, { Component, lazy } from 'react';
import Button from 'app/common/react/components/growth/unauth/Button/Button';
import cx from 'classnames';
import i18n from 'app/common/lib/i18n';
import LoginButton from 'app/common/react/components/growth/unauth/header/UnauthHeader/LoginButton';
import logo from 'app/common/react/components/growth/unauth/lib/images/logo';
import logStats from 'app/common/lib/logStats';
import PropTypes from 'prop-types';
import SafeSuspense from 'app/packages/safe-suspense';
import storage from 'app/common/lib/Storage';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import { compose } from 'redux';
import { EU_COOKIE_BAR_HEIGHT } from 'app/common/react/components/growth/unauth/header/EuCookieBar/EuCookieBar';
import { HEADER_ID } from 'app/common/react/components/Header/HeaderConstants';
import { HybridTiers } from 'app/common/react/components/growth/unauth/open/hybrid/hybridConstants';
import { sendFacebookPixel } from 'app/packages/facebook-pixel-utils';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import { Box, IconButton, Link } from 'gestalt';

const UnauthSettingFlyout = lazy(() =>
  import('app/common/react/components/growth/unauth/Settings/UnauthSettingFlyout'),
);

type OwnProps = {|
  alignHeader: ?boolean,
  autocorrect: React$Element<any>,
  children: React$Element<any>,
  container: string,
  hybridTier: ?string,
  isShowEUCookieBar: boolean,
  noButtons: ?boolean,
  numServerRenderedPins: number,
  onLoginClick: (e?: Event) => void,
  onSignupClick: (e: Event) => void,
  stickyHeader: ?boolean,
  showRightSection: ?boolean,
  isMobile: boolean,
  forceStaticHeader: ?boolean,
|};

type Props = {|
  ...OwnProps,
  seoUnauthExperiments: UnauthSeoExperiments,
|};

type State = {|
  headerStyle: ?string,
  userInfo: ?{|
    first_name: string,
    large_image_url: string,
  |},
  shouldShowSettingFlyout: boolean,
|};

const styles = {
  caret: {
    background: '#fff',
    transform: 'rotate(45deg)',
    position: 'absolute',
    top: '63.5px',
  },
  caretShadow: {
    boxShadow: '0px 1px 10px 0px rgba(153, 153, 153, 0.5)',
  },
  discoverText: {
    color: '#434343',
    fontSize: '18px',
    fontWeight: 'bold',
    marginLeft: '14px',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  leftHeaderContent: {
    display: 'flex',
    flex: '1',
    alignItems: 'center',
  },
  header: {
    top: '0',
    backgroundColor: '#fff',
    borderBottom: '1px solid #ccc',
    width: '100%',
    zIndex: 675, // The "follow" button on creator profiles has a z-index of 670.
  },
  headerContainer: {
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    margin: '0 32px',
  },
  alignedHeaderContainer: {
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    margin: '0 16px',
  },
  rightHeaderContent: {
    order: 1,
    WebkitOrder: 1,
    msFlexOrder: 1,
  },
  barButtonsWrapper: {
    display: 'inline-flex',
  },
  leftButton: {
    marginRight: '8px',
  },
  logoContainer: {
    cursor: 'pointer',
    display: 'block',
    float: 'left',
    height: '28px',
    width: '28px',
    verticalAlign: 'middle',
  },
  rightContentContainer: {
    position: 'relative',
  },
  signupButton: {
    display: 'inline-block',
    lineHeight: '36px',
    marginTop: '0px',
    padding: '0px',
    fontSize: '14px',
    width: '128px',
    height: '40px',
    borderRadius: '4px',
  },
  circleLogo: {
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  forgotAccountLink: {
    fontSize: '12px',
    fontWeight: 'bold',
    marginTop: '8px',
  },
  profilePic: {
    cursor: 'pointer',
    display: 'inline-block',
    width: '40px',
    verticalAlign: 'middle',
  },
  profilePicButtons: {
    display: 'inline-block',
    height: '40px',
    padding: '10px 0 0 16px',
    verticalAlign: 'middle',
  },
  continueLink: {
    color: '#333',
    cursor: 'pointer',
    display: 'block',
    fontSize: '14px',
    fontWeight: 'bold',
    lineHeight: '16px',
  },
  notYouLink: {
    color: '#333',
    cursor: 'pointer',
    display: 'block',
    fontSize: '12px',
    fontWeight: 'bold',
    lineHeight: '14px',
    paddingTop: '2px',
  },
};

const LOCALSTORAGE_UNAUTH_PUBLIC_PROFILE = 'unauthLoggedOutInfoStorage';

class UnauthHeader extends Component<Props, State> {
  static defaultProps = {
    stickyHeader: false,
    showRightSection: true,
    forceStaticHeader: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      headerStyle: null,
      userInfo: null,
      shouldShowSettingFlyout: false,
    };
  }

  componentDidMount() {
    this.setHeaderStyle();

    const { onLoginClick } = this.props;

    if (storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_INITIALLY_OPEN_LOGIN_MODAL)) {
      onLoginClick();
      storage.localStorage.removeItem(PCONST.LOCALSTORAGE_UNAUTH_INITIALLY_OPEN_LOGIN_MODAL);
    }

    window.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    window.removeEventListener('mousedown', this.handleClickOutside);
  }

  setHeaderStyle() {
    const userInfo = storage.localStorage.getItem(LOCALSTORAGE_UNAUTH_PUBLIC_PROFILE);
    if (userInfo) {
      this.setState({ headerStyle: 'personalized', userInfo });
    } else {
      this.setState({ headerStyle: 'default' });
    }
  }

  settingButtonRef: ?HTMLElement;
  unauthSettingFlyoutRef: ?HTMLElement;

  // handle setting button click
  handleSettingButtonClick = (event: SyntheticMouseEvent<>) => {
    event.preventDefault();
    event.stopPropagation();
    trackRegisterAction('unauth_web.unauth_header.setting_button.click');

    this.setState({ shouldShowSettingFlyout: !this.state.shouldShowSettingFlyout });
  };

  handleLogoClick = () => {
    trackRegisterAction('unauth_web.unauth_header.main_logo.click');
  };

  handleDiscoverTextClick = () => {
    trackRegisterAction('unauth_web.unauth_header.discovery_text.click');
  };

  handleClickOutside = (event) => {
    if (
      this.unauthSettingFlyoutRef &&
      this.settingButtonRef &&
      !this.unauthSettingFlyoutRef.contains(event.target) &&
      !this.settingButtonRef.contains(event.target)
    ) {
      this.setState({ shouldShowSettingFlyout: false });
    }
  };

  renderSignupButton(colorClass, additionalStyles) {
    if (!this.props.onSignupClick) {
      return null;
    }
    const classNames = cx(colorClass, 'headerSignupButton');

    return (
      <Box data-test-id="signupButton" key="signupButton">
        <Button
          className={classNames}
          numServerRenderedPins={this.props.numServerRenderedPins}
          onClick={(e) => {
            trackRegisterAction('unauth.signup_button.click');
            sendFacebookPixel('Lead', () => {
              logStats.increment('fb_tracking_pixel', 1, {
                event_category: 'Lead',
                event_name: 'Header Signup Button Click',
              });
            });
            this.props.onSignupClick(e);
          }}
          key="signup"
          styleOverrides={{ ...styles.signupButton, ...additionalStyles }}
        >
          {i18n._('Sign up', 'Text on button on the top bar that leads users to signing up.')}
        </Button>
      </Box>
    );
  }

  renderLoginButton(colorClass, additionalStyles) {
    return (
      <LoginButton
        key="loginButton"
        isMobile={this.props.isMobile}
        numServerRenderedPins={this.props.numServerRenderedPins}
        onClick={(e) => {
          trackRegisterAction('unauth.login_button.click');
          sendFacebookPixel('Lead', () => {
            logStats.increment('fb_tracking_pixel', 1, {
              event_category: 'Lead',
              event_name: 'Header Login Button Click',
            });
          });
          this.props.onLoginClick(e);
        }}
        colorClass={colorClass}
        containerStyles={additionalStyles}
      />
    );
  }

  renderSettingButton = () => (
    <React.Fragment key="settingButton">
      <div
        ref={(c) => {
          this.settingButtonRef = c;
        }}
      >
        <Box display="flex" marginLeft={2} marginTop={1} justifyContent="center">
          <IconButton
            accessibilityLabel={i18n._(
              'Setting button',
              'Text for the setting button in the header',
            )}
            iconColor="darkGray"
            icon="ellipsis"
            size="sm"
            onClick={({ event }) => {
              this.handleSettingButtonClick(event);
            }}
          />
        </Box>
      </div>
      {this.state.shouldShowSettingFlyout && (
        <SafeSuspense fallback={<div />}>
          <div
            ref={(c) => {
              this.unauthSettingFlyoutRef = c;
            }}
          >
            <UnauthSettingFlyout anchor={this.settingButtonRef} />
          </div>
        </SafeSuspense>
      )}
    </React.Fragment>
  );

  renderRightContent = () => {
    if (this.state.headerStyle === 'default' && !this.props.noButtons) {
      let loginButtonColor = 'lightGrey';
      let signupButtonColor = 'red';
      if (
        this.props.hybridTier === HybridTiers.tier1 &&
        (
          this.props.seoUnauthExperiments.v2ActivateExperiment('unauth_login_primary_cta') || ''
        ).startsWith('enabled')
      ) {
        loginButtonColor = 'red';
        signupButtonColor = 'lightGrey';
      }
      return [
        this.renderSignupButton(signupButtonColor, styles.leftButton),
        this.renderLoginButton(loginButtonColor),
        this.renderSettingButton(),
      ];
    } else {
      return null;
    }
  };

  render() {
    const {
      alignHeader,
      showRightSection,
      stickyHeader,
      forceStaticHeader,
      seoUnauthExperiments,
    } = this.props;

    const logoStyle = styles.logoContainer;
    const inSemanticGroup =
      (this.context.pageType === 'pin' &&
        (
          seoUnauthExperiments.v2GetGroup('pin_grid_style_format_optimization_pin_v3') || ''
        ).startsWith('enabled')) ||
      (this.context.pageType === 'board' &&
        (
          seoUnauthExperiments.v2GetGroup('pin_grid_style_format_optimization_board_v3') || ''
        ).startsWith('enabled'));

    // NOTE(neeraj): this prop should be removed once we migrate all pages
    // to using this header in the UnauthPageWrapper
    const stickyHeaderStyle = forceStaticHeader ? 'static' : 'absolute';
    const position = stickyHeader ? 'fixed' : stickyHeaderStyle;

    const headerStyle = {
      ...styles.header,
      position,
    };
    const logoSize = 28;

    const headerProps = {};
    const tagline = i18n._('Pinterest');
    if (alignHeader) {
      headerProps.style = styles.alignedHeaderContainer;
    } else {
      headerProps.style = styles.headerContainer;
    }

    if (this.props.isShowEUCookieBar && stickyHeader) {
      headerProps.style = { ...headerProps.style, marginTop: `${EU_COOKIE_BAR_HEIGHT}px` };
    }

    const innerHtmlHeader = (
      <div {...headerProps}>
        {showRightSection && (
          <div style={styles.rightHeaderContent}>
            <div style={styles.barButtonsWrapper}>
              <div
                className="UnauthHeader__rightContentContainer"
                style={styles.rightContentContainer}
              >
                {this.renderRightContent()}
              </div>
            </div>
          </div>
        )}
        <div style={styles.leftHeaderContent}>
          <Link href="/" onClick={this.handleLogoClick}>
            <span style={logoStyle}>{logo(logoSize, false)}</span>
          </Link>
          <a
            href="/"
            style={{ textDecoration: 'none' }}
            onClick={this.handleDiscoverTextClick}
            role="button"
          >
            <span className={'UnauthHeader__discoverText'} style={styles.discoverText}>
              {tagline}
            </span>
          </a>
          {this.props.children}
        </div>
      </div>
    );

    const Tag = inSemanticGroup ? 'header' : 'div';

    return (
      <Tag data-test-id="unauthHeader" id={HEADER_ID} style={headerStyle}>
        {innerHtmlHeader}
        {this.props.autocorrect}
      </Tag>
    );
  }
}

export default compose(withUnauthSeoExperiments)(UnauthHeader);

UnauthHeader.contextTypes = {
  pageType: PropTypes.string,
  isBot: PropTypes.bool.isRequired,
};
